import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { confirmar } from "@/components/ConfirmDialog";
import { Loader2, FlaskConical, ExternalLink, RefreshCw, Users, ClipboardCheck, CalendarClock, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TESTES } from "@/lib/tricolab";

/** Monitoramento do app Tricolab: acesso, uso, check-ups, resultados dos testes e ativos. Sem dados das clientes. */
const ESC = "#0f1f1a", VERDE = "#2f8f63";
const n = (x) => Number(x || 0).toLocaleString("pt-BR");
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");

function Kpi({ rotulo, valor, sub, icone: Icone, alerta }) {
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${alerta ? "border-amber-300" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{Icone && <Icone className="h-4 w-4" />}{rotulo}</div>
      <div className="mt-1 font-display text-3xl font-bold tabular-nums text-slate-900">{valor}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}
const Bloco = ({ titulo, nota, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="font-display text-lg font-semibold text-slate-900">{titulo}</h2>
    {nota && <p className="mb-3 text-xs text-slate-500">{nota}</p>}
    {children}
  </div>
);

export default function TricolabPainel() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const carregar = () => api.get("/admin/tricolab/painel").then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { carregar(); }, []);
  const liberar = async (v) => {
    if (!(await confirmar(v ? "Liberar o Tricolab para todas as alunas com o curso Tricolab ativo?" : "Desligar o acesso automático das alunas do curso Tricolab? Só as ativadas à mão continuam."))) return;
    setBusy(true);
    try { await api.put("/admin/tricolab/config", { ativo: v }); toast.success(v ? "Tricolab liberado para as alunas do curso." : "Acesso automático desligado."); carregar(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!d) return <div className="grid h-64 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const a = d.acesso, c = d.checkups;
  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-center gap-4 rounded-2xl p-6 text-white" style={{ background: `linear-gradient(135deg,${ESC},#1d3a31 60%,${VERDE})` }}>
        <span className="grid h-12 w-12 place-items-center rounded-xl bg-[#e4f1ea]"><FlaskConical className="h-7 w-7" style={{ color: ESC }} /></span>
        <div className="min-w-0 flex-1"><h1 className="font-display text-3xl font-bold tracking-wide">Tricolab<sup className="ml-0.5 text-sm">®</sup></h1>
          <p className="text-sm text-white/75">Monitoramento do app · atualizado em {d.gerado_em}</p></div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={carregar}><RefreshCw className="mr-1.5 h-4 w-4" /> Atualizar</Button>
          <a href="/tricolab" target="_blank" rel="noopener" className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold" style={{ color: ESC }}><ExternalLink className="h-4 w-4" /> Abrir o app</a>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <Switch checked={!!d.liberado} disabled={busy} onCheckedChange={liberar} />
        <div className="text-sm"><b>{d.liberado ? "Liberado para as alunas do curso Tricolab" : "Acesso automático desligado"}</b>
          <div className="text-xs text-slate-500">{d.liberado ? "Toda aluna com o curso Tricolab ativo (e as certificadas) entra no app; as novas matrículas entram sozinhas." : "Só entra quem foi ativada na ficha."} Ativação à mão: ficha da aluna ou professora → Tricolab.</div></div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icone={Users} rotulo="Com acesso" valor={n(a.com_acesso)} sub={`${n(a.pelo_curso)} pelo curso · ${n(a.ativadas)} ativadas à mão`} />
        <Kpi icone={Users} rotulo="Já usaram" valor={n(a.usaram)} sub={`${pct(a.usaram, a.com_acesso)} de quem tem acesso · ${n(a.nunca_usaram)} nunca usaram`} alerta={a.com_acesso > 0 && a.usaram / a.com_acesso < 0.3} />
        <Kpi icone={ClipboardCheck} rotulo="Clientes cadastradas" valor={n(d.clientes)} sub={`${n(a.com_salao)} profissionais com “Meu salão” preenchido`} />
        <Kpi icone={ClipboardCheck} rotulo="Check-ups" valor={n(c.checkups)} sub={`${n(c.validados)} validados · ${n(c.rascunhos)} em rascunho`} />
        <Kpi rotulo="Check-ups hoje" valor={n(c.hoje)} sub={`${n(c.d7)} nos últimos 7 dias · ${n(c.d30)} em 30 dias`} />
        <Kpi rotulo="Aceitaram o termo" valor={n(a.aceitaram_termo)} sub="termo de uso do app" />
        <Kpi icone={CalendarClock} rotulo="Reavaliações" valor={n(d.reavaliacoes.proximos_7)} sub={`nos próximos 7 dias · ${n(d.reavaliacoes.proximos_30)} em 30 dias · ${n(d.reavaliacoes.vencidas)} já passaram`} />
        <Kpi icone={AlertTriangle} rotulo="Fibras com alerta" valor={n(c.alertas_quimica)} sub="check-ups com proteína muito degradada ou metais elevados" alerta={c.alertas_quimica > 0} />
      </div>

      <Bloco titulo="Check-ups por dia" nota="Últimos 30 dias, pela data do atendimento.">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.serie} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="dia" tickFormatter={(x) => x.slice(8, 10) + "/" + x.slice(5, 7)} tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" minTickGap={18} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip formatter={(v) => [v, "check-ups"]} labelFormatter={(x) => x.split("-").reverse().join("/")} />
              <Bar dataKey="checkups" fill={VERDE} radius={[4, 4, 0, 0]} maxBarSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Bloco>

      <Bloco titulo="Resultados dos testes" nota="Só check-ups validados. Mostra o que mais aparece nos cabelos das clientes das alunas.">
        {c.validados === 0 ? <p className="text-sm text-slate-500">Ainda não há check-ups validados.</p> : (
          <div className="grid gap-4 md:grid-cols-2">{TESTES.map((t) => {
            const cont = d.testes[t.id] || {}; const total = Object.values(cont).reduce((s, x) => s + x, 0);
            return (
              <div key={t.id}>
                <div className="mb-1 text-sm font-semibold text-slate-800">{t.nome}</div>
                {t.op.map((o) => { const v = cont[o.v] || 0; return (
                  <div key={o.v} className="flex items-center gap-2 py-0.5 text-sm">
                    <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-black/10" style={{ background: o.cor }} />
                    <span className="w-44 shrink-0 truncate text-slate-600" title={o.t}>{o.t}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full" style={{ width: total ? `${(v / total) * 100}%` : 0, background: VERDE }} /></span>
                    <span className="w-16 shrink-0 text-right tabular-nums text-slate-700">{v} · {pct(v, total)}</span>
                  </div>); })}
              </div>);
          })}</div>
        )}
      </Bloco>

      <div className="grid gap-6 md:grid-cols-2">
        <Bloco titulo="Ativos mais escolhidos" nota="Nas fórmulas validadas (até 15).">
          {d.ativos.length === 0 ? <p className="text-sm text-slate-500">Ainda sem fórmulas validadas.</p> :
            <ol className="space-y-1.5 text-sm">{d.ativos.map((x, i) => (
              <li key={x.nome} className="flex items-center gap-2"><span className="w-5 text-right text-xs text-slate-400">{i + 1}</span><span className="flex-1 truncate">{x.nome}</span>
                <span className="h-2 w-24 overflow-hidden rounded-full bg-slate-100"><span className="block h-full rounded-full" style={{ width: `${(x.vezes / d.ativos[0].vezes) * 100}%`, background: VERDE }} /></span>
                <span className="w-8 text-right tabular-nums">{x.vezes}</span></li>))}</ol>}
        </Bloco>
        <Bloco titulo="Bases usadas" nota="Nas fórmulas validadas.">
          {d.bases.length === 0 ? <p className="text-sm text-slate-500">Ainda sem fórmulas validadas.</p> :
            <ul className="space-y-1.5 text-sm">{d.bases.map((x) => <li key={x.nome} className="flex justify-between gap-2"><span>{x.nome}</span><b className="tabular-nums">{x.vezes}</b></li>)}</ul>}
        </Bloco>
      </div>

      <Bloco titulo="Uso por profissional" nota="Quem mais usa o app. Não mostra dados das clientes.">
        {d.ranking.length === 0 ? <p className="text-sm text-slate-500">Ninguém usou ainda.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs text-slate-500"><th className="py-2">Profissional</th><th className="text-right">Clientes</th><th className="text-right">Check-ups</th><th className="text-right">Validados</th><th className="pl-3">Último uso</th><th className="pl-3">Meu salão</th></tr></thead>
            <tbody>{d.ranking.map((r) => (
              <tr key={r.nome + r.ultimo} className="border-b border-slate-100">
                <td className="py-2">{r.nome}{r.papel === "instructor" && <span className="ml-1.5 rounded bg-green-50 px-1.5 text-[10px] font-bold uppercase text-green-700">prof</span>}{r.papel === "admin" && <span className="ml-1.5 rounded bg-slate-100 px-1.5 text-[10px] font-bold uppercase text-slate-500">admin</span>}</td>
                <td className="text-right tabular-nums">{r.clientes}</td><td className="text-right tabular-nums">{r.checkups}</td><td className="text-right tabular-nums">{r.validados}</td>
                <td className="pl-3 text-slate-600">{r.ultimo ? r.ultimo.split("-").reverse().join("/") : "—"}</td>
                <td className="pl-3">{r.salao ? <span className="text-green-700">preenchido</span> : <span className="text-amber-700">falta</span>}</td>
              </tr>))}</tbody>
          </table></div>
        )}
      </Bloco>
    </div>
  );
}
